import React, { useMemo } from 'react'

import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ErrorBanner, spacing, Stack } from '@cash-design-system/react'
import { Instrument } from '@cash-web/protos/squareup/franklin/api/instrument.pb'
import { CardBlocker, CardStatus } from '@cash-web/protos/squareup/franklin/blockers/card_blocker.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import { KeyedCard } from '@cash-web/protos/squareup/franklin/common/keyed_card.pb'
import { CardBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { decodeInstrumentTokenToReplace, useOptimisticMutationLinkCard } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { FormCard } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

import { getLinkCardErrors } from '../../util/statusErrors'
import { BaseBlocker, BlockerProps } from '../BaseBlocker/BaseBlocker'
import { BlockerLinkBankAccount } from '../BlockerLinkBankAccount/BlockerLinkBankAccount'

const MarginBottom = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.m};
`

const debitCards = (instruments: Instrument[]): Instrument[] =>
  instruments?.filter(instrument => instrument.cash_instrument_type === CashInstrumentType.DEBIT_CARD)

const unsupportedCardMessage = (hasDebitCard: boolean, onlyBankAccount: boolean, t: TFunction) => {
  if (onlyBankAccount) {
    return t('Debit cards are not supported for standard deposits. Please link a bank account to continue.', {
      context: 'Error message for unsupported card',
    })
  } else if (hasDebitCard) {
    return t('Your card is not accepting instant deposits. Please link a different debit card.', {
      context: 'Error message for unsupported card',
    })
  } else {
    return t('Please link a debit card.', {
      context: 'Error message for unsupported card',
    })
  }
}

interface Props extends BlockerProps {
  blocker: CardBlocker
  supplement?: CardBlockerSupplement
  buttons?: React.ReactNode[]
}

export function BlockerCard({ blocker, skippable, buttons }: Props): React.ReactElement {
  const { t } = useTranslation()
  const { profile } = useAuth()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const flow = useFlowContext()
  const journeyContext = useJourneyContext()

  const threedsReturnUrl: string | undefined = useFeatureFlag('cash-web-3ds-redirect-url-test')

  const initiationDataFromJourney = journeyContext.active?.path
  const instrumentTokenToReplace = useMemo(() => {
    return initiationDataFromJourney ? decodeInstrumentTokenToReplace(initiationDataFromJourney) : undefined
  }, [initiationDataFromJourney])

  const { mutateAsync: mutateLinkCard } = useOptimisticMutationLinkCard({
    options: { flowToken: flow.active?.flowToken, clientScenario: flow.active?.clientScenario },
  })

  const form = useForm({ mode: 'onTouched' })

  const onSubmit = async (data: KeyedCard) => {
    // we need to modify the expiration to match the format expected by the API
    // see: https://prototype.sqprod.co/#/docs/squareup.franklin.common.KeyedCard%23expiration
    let expiration = data.expiration
    const expMatch = expiration?.replace('/', '').match(/.{1,2}/g)
    if (expMatch != null) {
      const [month, year] = expMatch
      expiration = `${year}${month}`
    }

    await handleBlockerResponse({
      promisedBlockerUpdate: mutateLinkCard({
        card: { ...data, expiration },
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
        instrument_type: CashInstrumentType.DEBIT_CARD,
        threeds_return_url: threedsReturnUrl ? threedsReturnUrl : undefined,
        instrumentTokenToReplace,
      }),
      errors: getLinkCardErrors(t),
    })
  }

  // DEBIT_CARD and BANK_ACCOUNT are the only instrument types that have been supported in this blocker so far. If more instrument
  // types are needed, the blocker's code will need to be updated.
  const supportedTypes = [CashInstrumentType.DEBIT_CARD, CashInstrumentType.BANK_ACCOUNT]
  if (!blocker.supported_instrument_types?.some(type => supportedTypes.includes(type))) {
    throw Error(
      `None of the options of [${blocker.supported_instrument_types?.join(',')}] are supported yet in Card Blocker.`
    )
  }
  if (blocker?.supported_instrument_types == null) {
    throw Error('No supported instrument types were provided for the Card Blocker.')
  }

  // Note: The `supplement?.main_text` is not being used for the title so that the web
  // can have more control of the title and subtitle.
  const title = t('Link bank using your debit card', { context: 'link debit card button title' })
  const subtitle = t('Linking an external account allows you to move money in and out of your Cash App balance.', {
    context: 'secondary text for linking a debit card',
  })

  const onlyBankAccount =
    blocker.supported_instrument_types.length === 1 &&
    blocker.supported_instrument_types[0] === CashInstrumentType.BANK_ACCOUNT

  const unsupportedCard = blocker.card_status === CardStatus.UNSUPPORTED_FOR_DEPOSIT_PREFERENCE
  const hasDebitCard = debitCards(profile?.instruments ?? []).length > 0
  const unsupportedError = unsupportedCard && unsupportedCardMessage(hasDebitCard, onlyBankAccount, t)

  return (
    <Stack space={spacing.m}>
      {unsupportedError ? (
        <MarginBottom>
          <ErrorBanner value={unsupportedError} />
        </MarginBottom>
      ) : null}
      {onlyBankAccount ? (
        <BlockerLinkBankAccount />
      ) : (
        <BaseBlocker
          mainText={title}
          secondaryText={subtitle}
          form={form}
          onSubmit={onSubmit}
          skippable={skippable}
          buttons={buttons}
        >
          <FormCard />
        </BaseBlocker>
      )}
    </Stack>
  )
}
